import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';
import _map from 'lodash/map';

import {
  GET_STORE_LIST,
  POST_STORE_SELECT,
  GET_STORE_BY_ID,
  GET_STORE_INSIGHTS,
  GET_TERMINAL_SHIPPING_METHODS,
} from 'services/api';
import { flatStore, rejectValue } from 'redux/helpers';
import {
  STORE,
  STORE_ALL,
  STORE_SELECT,
  STORE_GET_BY_ID,
  STORE_INSIGHTS,
  STORE_TERMINAL_SHIPPING_METHODS,
} from './types';

const javelinStoreAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export const initialState = javelinStoreAdapter.getInitialState({
  loading: false,
  current: null,
  insights: {
    orders_count: 0,
    orders_total_amount: 0,
    orders_avg_amount: 0,
  },
  terminalShippingMethods: [],
  currentStoreId: null,
});

export const getStoreList = createAsyncThunk(
  STORE_ALL,
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data, meta } = await GET_STORE_LIST();

      if (!!data.length) {
        const ids = _map(data, 'id');
        const storeId =
          !meta.selected_store_id || ids.indexOf(meta.selected_store_id) === -1
            ? data[0].id
            : meta.selected_store_id;

        if (
          getState().javelinStore.current === null ||
          storeId !== getState().javelinStore.current
        ) {
          await dispatch(selectStore(storeId));
        }
      }
      return { data, selected_store_id: meta.selected_store_id };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const selectStore = createAsyncThunk(
  STORE_SELECT,
  async (storeId, { dispatch, rejectWithValue, getState }) => {
    try {
      const { data } = await POST_STORE_SELECT(storeId);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getStoreById = createAsyncThunk(
  STORE_GET_BY_ID,
  async (storeId, { rejectWithValue }) => {
    try {
      const { data } = await GET_STORE_BY_ID(storeId);

      return flatStore(data);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getStoreInsights = createAsyncThunk(
  STORE_INSIGHTS,
  async (_, { rejectWithValue, getState }) => {
    try {
      const storeId = getState().javelinStore.current;
      const { data } = await GET_STORE_INSIGHTS(storeId);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTerminalShippingMethods = createAsyncThunk(
  STORE_TERMINAL_SHIPPING_METHODS,
  async (payload, { rejectWithValue, getState }) => {
    try {

      const { storeId, country: countryArg } = payload;

      let country = countryArg || null;
      const stateStore = getState();
      const cartEntities = Object.values(stateStore.cart.entities);

      if (!country && cartEntities.length) {
        country = cartEntities[0].productable.country;
      }

      if (!country) {
        country = stateStore.checkoutForm?.shopperDetails?.phone_country?.code;
      }

      const { data } = await GET_TERMINAL_SHIPPING_METHODS(storeId, country);

      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const javelinStoreSlice = createSlice({
  name: STORE,
  initialState,
  reducers: {
    captureCurrentStoreId(state, { payload }) {
      state.currentStoreId = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStoreList.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStoreList.fulfilled, (state, { payload }) => {
        state.loading = false;
        javelinStoreAdapter.setAll(state, payload.data);
      })
      .addCase(getStoreInsights.pending, (state, { payload }) => {
        state.insights.loading = true;
      })
      .addCase(getStoreInsights.rejected, (state, { payload }) => {
        state.insights.loading = true;
      })
      .addCase(getStoreInsights.fulfilled, (state, { payload }) => {
        state.insights = {
          loading: false,
          ...payload,
        };
      })
      .addCase(getStoreList.rejected, (state) => {
        state.loading = false;
      })
      .addCase(selectStore.pending, (state, action) => {
        state.loading = true;
      })
      .addCase(selectStore.rejected, (state) => {
        state.loading = false;
      })

      .addCase(selectStore.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.id) {
          javelinStoreAdapter.upsertOne(state, action.payload);
          state.current = action.payload.id;
          state.insights = initialState.insights;
        }
      })
      .addCase(getTerminalShippingMethods.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getTerminalShippingMethods.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTerminalShippingMethods.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.terminalShippingMethods = payload;
      })
      .addDefaultCase((state) => state);
  },
});

export const storeListSelector = javelinStoreAdapter.getSelectors(
  (state) => state.javelinStore
);

export const storeLoadingState = (state) => state.javelinStore.loading;

export const currentStoreSelector = (state) =>
  storeListSelector.selectById(state, state.javelinStore.current) || {};

export const currentStoreNameIdSelector = createDraftSafeSelector(
  (state) => state,
  (state) => {
    const _currentStore = storeListSelector.selectById(
      state,
      state.javelinStore.current
    );
    return _currentStore
      ? {
        id: _currentStore.id,
        name: _currentStore.name,
      }
      : {};
  }
);

export const { captureCurrentStoreId } = javelinStoreSlice.actions;

export const terminalShippingMethodsSelector = (state) => state.javelinStore.terminalShippingMethods;

export default javelinStoreSlice.reducer;
